import { FC, useEffect, useRef, useState } from 'react';

import {
  AppsIcon,
  ClockIcon,
  ConsoleIcon,
  TTranslator,
} from '@smartshell/ui-kit';

import { TLocalesKeys } from 'localization';

import s from './ShellTopPanel.module.scss';

export type TShellTopPanelTab = 'games' | 'applications';

export interface IShellTopPanelSession {
  id: number;
  timeLeft: number;
}

interface IShellTopPanelProps {
  hostAlias: string;
  session?: IShellTopPanelSession;
  t: TTranslator<TLocalesKeys>;
  onTabChange: (tab: TShellTopPanelTab) => void;
  onLoginClick: () => void;
  onTariffsClick: () => void;
}

interface IIndicatorPosition {
  left: number;
  width: number;
}

const formatTimeLeft = (seconds: number): string => {
  const duration = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const remainingSeconds = duration % 60;

  return [hours, minutes, remainingSeconds]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
};

export const ShellTopPanel: FC<IShellTopPanelProps> = ({
  hostAlias,
  session,
  t,
  onTabChange,
  onLoginClick,
  onTariffsClick,
}) => {
  const [activeTab, setActiveTab] = useState<TShellTopPanelTab>('games');
  const [indicator, setIndicator] = useState<IIndicatorPosition>();
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Partial<Record<TShellTopPanelTab, HTMLButtonElement>>>(
    {}
  );

  useEffect(() => {
    const updateIndicator = (): void => {
      const tabList = tabListRef.current;
      const activeTabElement = tabRefs.current[activeTab];

      if (!tabList || !activeTabElement) return;

      setIndicator({
        left: activeTabElement.offsetLeft,
        width: activeTabElement.offsetWidth,
      });
    };

    let indicatorFrame = window.requestAnimationFrame(() => {
      indicatorFrame = window.requestAnimationFrame(updateIndicator);
    });
    window.addEventListener('resize', updateIndicator);

    return () => {
      window.cancelAnimationFrame(indicatorFrame);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [activeTab]);

  const selectTab = (tab: TShellTopPanelTab): void => {
    setActiveTab(tab);
    onTabChange(tab);
  };

  return (
    <header className={s.ssShellTopPanel}>
      <div
        ref={tabListRef}
        className={s.ssShellTopPanelTabs}
        role="tablist"
        aria-label={t('shell.tabs.label')}
      >
        {indicator && (
          <span
            className={s.ssShellTopPanelTabIndicator}
            style={{
              transform: `translateX(${indicator.left}px)`,
              width: indicator.width,
            }}
            aria-hidden
          />
        )}
        <button
          ref={(node) => {
            tabRefs.current.games = node ?? undefined;
          }}
          className={s.ssShellTopPanelTab}
          type="button"
          role="tab"
          aria-selected={activeTab === 'games'}
          onClick={() => selectTab('games')}
        >
          <ConsoleIcon color="currentColor" size="l" />
          <span>{t('shell.tabs.games')}</span>
        </button>
        <button
          ref={(node) => {
            tabRefs.current.applications = node ?? undefined;
          }}
          className={s.ssShellTopPanelTab}
          type="button"
          role="tab"
          aria-selected={activeTab === 'applications'}
          onClick={() => selectTab('applications')}
        >
          <AppsIcon color="currentColor" size="l" />
          <span>{t('shell.tabs.applications')}</span>
        </button>
      </div>
      <div
        className={s.ssShellTopPanelSession}
        data-session-active={String(Boolean(session))}
      >
        <div className={s.ssShellTopPanelMessage}>
          <span className={s.ssShellTopPanelHostAlias}>{hostAlias}</span>
          <span className={s.ssShellTopPanelDivider} aria-hidden />
          <span className={s.ssShellTopPanelSessionStatus}>
            {session ? t('shell.session.active') : t('shell.status.online')}
          </span>
        </div>
        {session ? (
          <>
            <time
              className={s.ssShellTopPanelTimeLeft}
              dateTime={`PT${Math.max(0, Math.floor(session.timeLeft))}S`}
            >
              <ClockIcon color="currentColor" size="s" />
              {formatTimeLeft(session.timeLeft)}
            </time>
            <button
              className={s.ssShellTopPanelTariffsButton}
              type="button"
              onClick={onTariffsClick}
            >
              {t('tariffs')}
            </button>
          </>
        ) : (
          <button
            className={s.ssShellTopPanelLoginButton}
            type="button"
            onClick={onLoginClick}
          >
            {t('shell.guest.login')}
          </button>
        )}
      </div>
    </header>
  );
};
